import {
  ArrowRight,
  CheckCircle,
  ChevronDown,
  CircleCheck,
  SearchIcon,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import { Checkbox } from "@components/ui/checkbox";
import { Label } from "@components/ui/label";

const HIRING_COMPANIES = [
  {
    label: "Google",
    count: 1,
  },
  {
    label: "Northbridge Finance",
    count: 0,
  },
  {
    label: "HelioTech Group",
    count: 0,
  },
];

const ImportRequirementForm = () => {
  const [selectedRequirement, setSelectedRequirement] = useState(1);
  const [isHiringCompanyDropdownOpened, setIsHiringCompanyDropdownOpened] =
    useState(false);
  const [isHiringCompanyVisible, setIsHiringCompanyVisible] = useState(true);

  return (
    <>
      {/* Partnered requirement library */}
      <div className="rounded-md border">
        <div className="flex items-center justify-between border-b px-4 py-3 bg-slate-100">
          <div className="flex flex-col">
            <span className="font-bold text-xss text-light-black uppercase tracking-widest">
              Partnered requirement library
            </span>
            <span className="text-md font-semibold text-black">
              Select an approved requirement
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xsss font-bold text-light-black">
              Hiring company
            </span>
            <div className="flex items-center gap-1 px-2 rounded-md h-9 bg-white border w-56">
              <SearchIcon size={14} color="lightgray" />
              <input
                className="outline-none text-xss font-medium flex-1"
                placeholder="Search"
              />
              <DropdownMenu
                open={isHiringCompanyDropdownOpened}
                onOpenChange={setIsHiringCompanyDropdownOpened}
              >
                <DropdownMenuTrigger className="border-l pl-1 outline-none">
                  <ChevronDown className="w-4 h-4" color="gray" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white" align="end">
                  {HIRING_COMPANIES?.map((hiringCompany) => {
                    return (
                      <DropdownMenuItem
                        key={hiringCompany?.label}
                        className="flex items-center hover:cursor-pointer hover:bg-slate-50 gap-1 w-64"
                        onSelect={() => {
                          //   console.log("durationOption", durationOption);
                          //   field.onChange(durationOption);
                        }}
                      >
                        <div className="h-7 w-7 rounded-md bg-black text-white font-bold text-xs flex items-center justify-center">
                          {hiringCompany?.label?.charAt(0)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xss font-semibold leading-3">{hiringCompany?.label}</span>
                          <span className="text-xsss font-medium text-light-black leading-4">{hiringCompany?.count > 0 ?'1 approved requirement':'No approved requirements'}</span>
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {selectedRequirement ? (
          <div className="flex flex-col">
            <div className="bg-gray-50 p-4">
              <div className="flex items-center justify-between bg-white border border-black rounded-md p-4">
                <div className="flex items-center gap-3.5">
                  <div className="rounded-md bg-charcoal h-10 w-10 flex items-center justify-center text-white font-semibold text-xl">
                    G
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xss font-medium text-gray-400">
                      REQ-GGL-1048 · Received 06 Aug 2026
                    </span>
                    <span className="font-semibold text-sm">
                      Cyber Security Analyst
                    </span>
                    <span className="text-xss font-medium text-light-black">
                      Google · Permanent · London hybrid
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 border-l pl-4">
                  <div className="flex flex-col items-center gap-1">
                    <div className="rounded-full text-xss font-semibold text-green-700 bg-gray-100 w-max px-2 py-0.5">
                      Approved to recruit
                    </div>
                    <span className="text-xsss font-medium text-light-black">
                      3 skill requirements
                    </span>
                  </div>

                  <CircleCheck
                    size={22}
                    strokeWidth={1.5}
                    fill="#475569"
                    stroke="white"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[1.2fr_0.8fr] border-t p-4">
              <span className="text-xss font-medium text-light-black">
                <span className="font-bold">Included</span> Role facts,
                hiring-company comments, job description draft and contextual
                skill requirements.
              </span>
              <button className="bg-black rounded-md flex items-center text-xss font-semibold text-white h-8 gap-5 px-3 hover:bg-black/85 w-max ml-auto">
                Import requirement details
                <ArrowRight size={14} color="white" />
              </button>
            </div>

            <div className="flex items-center bg-green-100 border-t px-4 py-2.5 gap-3">
              <CircleCheck
                size={24}
                fill="#239447"
                stroke="white"
                strokeWidth={1.5}
              />
              <div className="flex flex-col">
                <span className="text-xs font-semibold">
                  Requirement details imported
                </span>
                <span className="text-xss font-medium text-light-black">
                  The source reference remains attached to this draft for review
                  and audit context.
                </span>
              </div>
            </div>
          </div>
        ) : (
          // Empty placeholder
          <div className="bg-[#fbfcfc] border-t p-5">
            <div className="flex flex-col items-center justify-center h-28 bg-white border border-dashed border-light-grey rounded-md">
              <span className="font-semibold text-xs text-black">
                No approved requirements
              </span>
              <span className="font-medium text-xss text-light-black">
                This partnered hiring company has not shared an approved
                requirement with your agency.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Requirement Context */}
      <div className="rounded-md border">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              02
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">Recruitment context</span>
              <span className="text-xss font-medium text-light-black">
                Confirm the hiring-company relationship attached to this draft.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-gray-600 uppercase bg-gray-100">
            Agency record
          </div>
        </div>

        <div className="grid grid-cols-[0.9fr_0.9fr_1.2fr] gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-slate-600">
              Hiring company
            </label>
            <CustomInputV2 placeholder="Company name" />
            <span className="text-xsss text-light-black font-medium">
              Selected from the partnered requirement library.
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-slate-600">
              Hiring-company requirement
            </label>
            <CustomInputV2 placeholder="Requirement Code" />
            <span className="text-xsss text-light-black font-medium">
              The source reference remains private to the recruitment workflow.
            </span>
          </div>
          <div className="flex flex-col rounded-md bg-slate-50 p-3 border gap-2">
            <label className="text-xsss font-bold text-slate-600 uppercase tracking-wider">
              Company-name visibility
            </label>
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="hiring-company-visibility"
                checked={isHiringCompanyVisible}
                onCheckedChange={(checked) =>
                  setIsHiringCompanyVisible(checked === true)
                }
                className=""
              />
              <div className="flex flex-col gap-2 mt-0.5">
                <Label
                  htmlFor="hiring-company-visibility"
                  className="text-xss font-semibold text-black"
                >
                  Show the hiring company on the candidate job post
                </Label>
                <span className="text-xsss text-light-black font-medium">
                  Turn this off for a confidential search. The requirement
                  reference is never published; review the job copy for other
                  identifying detail.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Source Note */}
      <div className="flex gap-5 border-l-[3px] border-slate-600 bg-slate-100 px-4 py-3.5 mt-4">
        <span className="uppercase font-bold text-xss text-slate-600 shrink-0">
          Source note
        </span>
        <span className="text-xss font-medium text-light-black">
          Google requested an operational security specialist who can connect
          incident response, governance and stakeholder communication. This
          comment is private to your agency and will not be published.
        </span>
      </div>
    </>
  );
};

export default ImportRequirementForm;
